import React, { useState, FormEvent, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { httpsCallable } from '@/lib/callableProxy';
import { functions as firebaseFunctions } from '@/config/firebase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { toast as sonnerToast } from "sonner";
import { Loader2, PlusCircle, LogOut } from 'lucide-react';
import AppLogo from '@/assets/logo.svg?react';
import { useNavigate } from 'react-router-dom';

const CreateWorkspacePage: React.FC = () => {
  const { fetchUserSettings, logout, userSettings } = useAuth();
  const [name, setName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    const checkAndSetup = async () => {
      if (userSettings?.activeWorkspace) {
        navigate('/studio', { replace: true });
        return;
      }

      try {
        await fetchUserSettings();
      } catch (e) {
        console.error("Workspace auto-check failed", e);
      } finally {
        if (isMounted) setIsChecking(false);
      }
    };

    checkAndSetup();

    return () => { isMounted = false; };
  }, [fetchUserSettings, navigate, userSettings]);

  useEffect(() => {
    if (userSettings?.activeWorkspace) {
        navigate('/studio', { replace: true });
    }
  }, [userSettings, navigate]);

  const handleCreate = async (e: FormEvent) => {
    e.preventDefault();
    if (name.trim().length < 3) {
      sonnerToast.error("Name must be at least 3 characters.");
      return;
    }
    setIsCreating(true);
    try {
      const createFn = httpsCallable(firebaseFunctions, 'createWorkspace');
      await createFn({ name, switchToActive: true });
      await fetchUserSettings(); 
      sonnerToast.success("Space created!");
    } catch (error: any) {
      sonnerToast.error(`Error: ${error.message}`);
      setIsCreating(false);
    }
  };

  if (isChecking) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-[#f8f5ef] p-4">
           <div className="flex flex-col items-center gap-6 animate-in fade-in zoom-in duration-500">
              <span className="grid h-12 w-12 place-content-center rounded-xl border border-slate-200 bg-slate-950 shadow-sm animate-pulse">
                <AppLogo className="h-6 w-6" />
              </span>
              <div className="flex items-center gap-3 text-slate-600">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span className="font-medium">Setting up your space...</span>
              </div>
           </div>
        </div>
      );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#f8f5ef] p-4 animate-in fade-in duration-500">
       <div className="mb-8 flex items-center gap-2.5 rounded-2xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
          <span className="grid h-9 w-9 place-content-center rounded-xl border border-slate-200 bg-slate-950 shadow-sm">
            <AppLogo className="h-5 w-5" />
          </span>
          <span className="text-xl font-semibold tracking-[-0.03em]">LendLifted</span>
       </div>

       <Card className="w-full max-w-md border-slate-200/80 bg-white/95 shadow-[0_24px_60px_-38px_rgba(15,23,42,0.35)]">
          <CardHeader className="text-center">
             <div className="mx-auto mb-4 w-fit rounded-full bg-slate-900/5 p-3 text-slate-900">
                <PlusCircle size={32} />
             </div>
             <CardTitle className="text-xl tracking-[-0.02em] text-slate-950">Name Your Space</CardTitle>
             <CardDescription className="text-slate-600">
                Create your seller workspace to manage quotes and financing checkout.
             </CardDescription>
          </CardHeader>
          <CardContent>
             <form onSubmit={handleCreate} className="space-y-4">
                <div className="space-y-2">
                    <Label htmlFor="ws-name" className="text-slate-800">Space Name</Label>
                    <Input
                        id="ws-name" 
                        placeholder="e.g. Downtown Studio"
                        value={name} 
                        onChange={e => setName(e.target.value)} 
                        disabled={isCreating}
                        autoFocus
                        className="rounded-xl border-slate-200 bg-white focus-visible:ring-slate-300"
                    />
                </div>
                <Button type="submit" className="w-full rounded-xl bg-slate-900 text-white hover:bg-slate-800" disabled={isCreating || name.length < 3}>
                    {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin"/>}
                    Create Space
                </Button>
             </form>
          </CardContent>
          <CardFooter className="justify-center border-t border-slate-100 pt-6">
             <Button variant="ghost" size="sm" onClick={() => logout()} className="text-slate-500 hover:bg-slate-50 hover:text-red-600">
                <LogOut className="mr-2 h-4 w-4" /> Sign Out
             </Button>
          </CardFooter>
       </Card>
    </div>
  );
};

export default CreateWorkspacePage;
